import {
  useState, useCallback, useEffect, useContext
} from 'react';

import {
  getAcquisitionStatusNursingCareList,
  getNursingCareDigestionItemsDetail,
  downloadNursingCareHistoryCsv,
} from 'api/holidayManagement';
import AcquisitionStatusNursingCareDomain from 'domain/master/holidayManagement/nursingHolidayDigestion';
import { HolidayManagementContext } from './../../HolidayManagementForm'
import useToastNotification from 'hooks/useToastNotification';
import moment from 'moment';

export interface DetailItem {
  digestedDate: any,
  digestedNum: any,
}

export const useAcquisitionStatusForm = () => {
  const sessionStorageOrgCode = sessionStorage.getItem('loginUser.orgCode') || '';
  const [acquisitionStatusFormList, setAcquisitionStatusFormList] = useState<Array<any>>([]);
  const [noManager, setNoManager] = useState(false);
  const [orgCode, setOrgCode] = useState(sessionStorageOrgCode);
  const [targetDateFrom, setTargetDateFrom] = useState<Date>(moment().subtract(1,'year').add(1, 'days').toDate());
  const [targetDateTo, setTargetDateTo] = useState<Date>(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [holidayType, setHolidayType] = useState('7');
  const holidayOptions = [
    {
      value : '7',
      label: '介護休暇'
    },
    {
      value : '8',
      label: '子の看護休暇'
    }
  ];

  const [detailItems, setDetailItems] = useState<DetailItem[]>([]);

  const contextObject = useContext(HolidayManagementContext);
  const { errorNotification } = useToastNotification();

  // データの取得
  const fetchData = useCallback(async () => {
    contextObject.setIsLoading(true);
    const response = await getAcquisitionStatusNursingCareList(
      orgCode !== 'all' ? orgCode : '',
      targetDateFrom,
      targetDateTo,
      Number(holidayType)
    );
    contextObject.setIsLoading(false);
    setAcquisitionStatusFormList(response.map((result) => new AcquisitionStatusNursingCareDomain(result)));
  }, [orgCode, targetDateFrom, targetDateTo, holidayType]);

  //
  const callGetAcquisitionStatusDetail = useCallback(async (staffCode: string) => {
    try {
      await getNursingCareDigestionItemsDetail(
        staffCode,
        Number(holidayType),
        String(moment(targetDateFrom).format('YYYY/MM/DD')),
        String(moment(targetDateTo).format('YYYY/MM/DD')),
      ).then((response: any) => {
        setDetailItems(response);
      });
    } catch (error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const listErr = error.response.data.errors;
        listErr.map((element: any) => errorNotification(element.defaultMessage));
      } else {
        errorNotification('サーバー側でエラーが発生しました。');
        throw error;
      }
    }
  }, [errorNotification, targetDateFrom, targetDateTo, holidayType]);

  useEffect(() => {
    fetchData();
  }, [fetchData, noManager, orgCode, targetDateFrom, targetDateTo, holidayType]);

  const toggleNoManager = useCallback(() => {
    setNoManager(!noManager);
  }, [noManager]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return {
    acquisitionStatusFormList,
    toggleNoManager,
    orgCode,
    setOrgCode,
    targetDateFrom,
    setTargetDateFrom,
    targetDateTo,
    setTargetDateTo,
    modalOpen,
    setModalOpen,
    closeModal,
    holidayType,
    setHolidayType,
    holidayOptions,
    detailItems,
    callGetAcquisitionStatusDetail
  };
};

export const useDownloadNursingCareHistory = (
  orgCode: string,
  targetDateFrom: Date,
  targetDateTo: Date,
) => {
  const [blocking, setBlocking] = useState(false);

  const downloadCsv = useCallback((holidayType: string) => {
    setBlocking(true);
    downloadNursingCareHistoryCsv(
      Number(holidayType),
      orgCode !== 'all' ? orgCode : '',
      targetDateFrom,
      targetDateTo,
    ).then((response) => {
      setBlocking(false);
    });
  }, [orgCode, targetDateFrom, targetDateTo]);

  return {
    downloadCsv,
    blocking
  };
};

export default {
  useAcquisitionStatusForm,
};
